/**
 * Reusable Framer Motion primitives for CRVD's cinematic scroll-driven feel.
 * Inspired by StringTune-style sequential reveals + letter-by-letter text.
 */
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

/* ---------- Letter-by-letter text reveal ---------- */
export const AnimatedText = ({
  text,
  className = "",
  as: As = "span",
  stagger = 0.025,
  delay = 0,
  once = true,
}) => {
  const chars = Array.from(text);
  return (
    <As
      className={className}
      aria-label={text}
      style={{ display: "inline-block" }}
    >
      {chars.map((ch, i) => (
        <motion.span
          key={i}
          aria-hidden="true"
          style={{ display: "inline-block", whiteSpace: "pre" }}
          initial={{ opacity: 0, y: "0.55em", filter: "blur(8px)" }}
          whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          viewport={{ once, amount: 0.5 }}
          transition={{
            delay: delay + i * stagger,
            duration: 0.6,
            ease: [0.2, 0.6, 0.1, 1],
          }}
        >
          {ch === " " ? "\u00A0" : ch}
        </motion.span>
      ))}
    </As>
  );
};

/* ---------- Word-by-word reveal (use for long copy) ---------- */
export const AnimatedWords = ({
  text,
  className = "",
  stagger = 0.06,
  delay = 0,
}) => {
  const words = text.split(" ");
  return (
    <span className={className} aria-label={text}>
      {words.map((w, i) => (
        <motion.span
          key={i}
          aria-hidden="true"
          style={{ display: "inline-block", marginRight: "0.28em" }}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{
            delay: delay + i * stagger,
            duration: 0.55,
            ease: [0.2, 0.6, 0.1, 1],
          }}
        >
          {w}
        </motion.span>
      ))}
    </span>
  );
};

/* ---------- Slide-in container (single element reveal) ---------- */
export const Reveal = ({
  children,
  delay = 0,
  y = 32,
  className = "",
  once = true,
  amount = 0.3,
}) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once, amount }}
    transition={{ delay, duration: 0.7, ease: [0.2, 0.6, 0.1, 1] }}
  >
    {children}
  </motion.div>
);

/* ---------- Stagger reveal for lists / grids ---------- */
export const StaggerList = ({ children, className = "", stagger = 0.08, delay = 0 }) => (
  <motion.div
    className={className}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.2 }}
    variants={{
      hidden: {},
      visible: { transition: { staggerChildren: stagger, delayChildren: delay } },
    }}
  >
    {children}
  </motion.div>
);

export const StaggerItem = ({ children, className = "", y = 24 }) => (
  <motion.div
    className={className}
    variants={{
      hidden: { opacity: 0, y, filter: "blur(6px)" },
      visible: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.7, ease: [0.2, 0.6, 0.1, 1] } },
    }}
  >
    {children}
  </motion.div>
);

/* ---------- Image clip-path reveal ---------- */
export const ImageReveal = ({ children, className = "", direction = "left" }) => {
  const initial =
    direction === "left"
      ? { clipPath: "inset(0 100% 0 0)" }
      : direction === "right"
      ? { clipPath: "inset(0 0 0 100%)" }
      : { clipPath: "inset(100% 0 0 0)" };
  return (
    <motion.div
      className={className}
      initial={initial}
      whileInView={{ clipPath: "inset(0 0 0 0)" }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 1.1, ease: [0.2, 0.6, 0.1, 1] }}
      style={{ display: "block", width: "100%", height: "100%" }}
    >
      {children}
    </motion.div>
  );
};

/* ---------- Parallax slow scroll on element ---------- */
export const ParallaxBox = ({ children, range = 80, className = "" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-range, range]);
  return (
    <motion.div ref={ref} className={className} style={{ y }}>
      {children}
    </motion.div>
  );
};

/* ---------- Section page transition wrapper ---------- */
export const PageFade = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -16 }}
    transition={{ duration: 0.5, ease: [0.2, 0.6, 0.1, 1] }}
  >
    {children}
  </motion.div>
);

/* ---------- Hero counter (eg. weight/price) ---------- */
export const CountUp = ({ value, prefix = "", suffix = "", className = "" }) => {
  const ref = useRef(null);
  return (
    <motion.span
      ref={ref}
      className={className}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
    >
      {prefix}
      <motion.span
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        {value}
      </motion.span>
      {suffix}
    </motion.span>
  );
};
